import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AppIcon from "../assets/images/SwaptoonIcon.jpg";
import {
  FaceRetouchingNatural,
  Groups,
  Favorite,
  ChildCare,
  AutoAwesome,
  Apps,
} from "@mui/icons-material";
import { useMoreApps } from "../context/MoreAppsContext";

const CustomNavbar: React.FC = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredPath, setHoveredPath] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { openMoreApps, apps } = useMoreApps();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (paths: string[]) => paths.includes(location.pathname);

  const navItems = [
    {
      path: "/",
      paths: ["/", "/faceswap"],
      label: "Face Swap",
      icon: <FaceRetouchingNatural sx={{ fontSize: 19 }} />,
    },
    {
      path: "/multiple-faceswap",
      paths: ["/multiple-faceswap"],
      label: "Multi Face Swap",
      icon: <Groups sx={{ fontSize: 19 }} />,
    },
    {
      path: "/prewedding-faceswap",
      paths: ["/prewedding-faceswap"],
      label: "Pre-Wedding",
      icon: <Favorite sx={{ fontSize: 19 }} />,
    },
    {
      path: "/babyshower-faceswap",
      paths: ["/babyshower-faceswap"],
      label: "Baby Shower",
      icon: <ChildCare sx={{ fontSize: 19 }} />,
    },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 w-full transition-all duration-300"
      style={{
        background: isScrolled ? "rgba(9, 13, 22, 0.9)" : "transparent",
        backdropFilter: isScrolled ? "blur(16px)" : "none",
        WebkitBackdropFilter: isScrolled ? "blur(16px)" : "none",
        borderBottom: isScrolled ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid transparent",
        boxShadow: isScrolled ? "0 10px 30px rgba(0, 0, 0, 0.3)" : "none",
        paddingTop: isScrolled ? "0.75rem" : "1.25rem",
        paddingBottom: isScrolled ? "0.75rem" : "1.25rem",
        zIndex: 1050,
      }}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between relative">
        {/* Brand Lockup */}
        <Link
          to="/"
          className="flex items-center gap-3 text-decoration-none group select-none"
        >
          <div className="relative p-0.5 rounded-xl bg-gradient-to-tr from-indigo-500 via-purple-500 to-pink-500 shadow-md transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_15px_rgba(139,92,246,0.5)]">
            <img
              src={AppIcon}
              alt="SwapToon AI"
              className="h-9 w-9 rounded-[10px] object-cover"
            />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xl font-extrabold premium-brand-text select-none">
                SwapToon
              </span>
              <span 
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-indigo-200"
                style={{
                  background: "rgba(99, 102, 241, 0.15)",
                  border: "1px solid rgba(99, 102, 241, 0.3)",
                }}
              >
                <AutoAwesome sx={{ fontSize: 10 }} /> AI Studio
              </span>
            </div>
            <span className="text-[10px] text-slate-400 font-medium tracking-wide">
              Fast, fun, polished face swaps
            </span>
          </div>
        </Link>

        {/* Right side: Nav Links + CTA (Desktop) */}
        <div className="flex items-center gap-4 lg:gap-6">
          {/* Navigation Links */}
          <nav
            className="hidden lg:flex items-center gap-1"
            onMouseLeave={() => setHoveredPath(null)}
          >
            {navItems.map((item) => {
              const active = isActive(item.paths);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onMouseEnter={() => setHoveredPath(item.path)}
                  className="relative px-4 py-2 text-[14px] font-bold tracking-wide text-slate-300 hover:text-white transition-colors duration-200 d-inline-flex align-items-center gap-2 text-decoration-none rounded-xl select-none"
                >
                  {/* Floating Hover Indicator */}
                  {hoveredPath === item.path && (
                    <motion.span
                      layoutId="hoverNavIndicator"
                      className="absolute inset-0 bg-white/5 rounded-xl -z-10"
                      transition={{ type: "spring", stiffness: 350, damping: 25 }}
                    />
                  )}
                  {/* Sliding Underline Active Indicator */}
                  {active && (
                    <motion.span
                      layoutId="activeNavIndicator"
                      className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full"
                      style={{
                        background: "linear-gradient(90deg, #6366f1, #ec4899)",
                        boxShadow: "0 0 8px rgba(99, 102, 241, 0.8)",
                      }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <span className="flex items-center gap-1.5">
                    {item.icon}
                    <span>{item.label}</span>
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* More Apps Trigger Button (Desktop) */}
          <button
            onClick={openMoreApps}
            id="more-apps-nav-btn"
            className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all cursor-pointer border-0 select-none shadow-lg shadow-indigo-500/20 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(168, 85, 247, 0.8))",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Apps sx={{ fontSize: 18 }} />
            <span>More Apps</span>
            {apps.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-white/20 text-white font-extrabold">
                {apps.length}
              </span>
            )}
          </button>

          {/* Mobile Menu Toggle Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex lg:hidden flex-col items-center justify-center gap-1.5 h-10 w-10 rounded-xl transition-all select-none p-0 cursor-pointer border-0"
            style={{
              background: "rgba(15, 23, 42, 0.6)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
            aria-label="Toggle Navigation Menu"
          >
            <motion.span
              variants={{
                closed: { rotate: 0, y: 0 },
                open: { rotate: 45, y: 6 },
              }}
              animate={isOpen ? "open" : "closed"}
              className="w-5 h-0.5 bg-white rounded"
              style={{ originY: "center" }}
              transition={{ duration: 0.2 }}
            />
            <motion.span
              variants={{
                closed: { opacity: 1, scale: 1 },
                open: { opacity: 0, scale: 0 },
              }}
              animate={isOpen ? "open" : "closed"}
              className="w-5 h-0.5 bg-white rounded"
              transition={{ duration: 0.2 }}
            />
            <motion.span
              variants={{
                closed: { rotate: 0, y: 0 },
                open: { rotate: -45, y: -6 },
              }}
              animate={isOpen ? "open" : "closed"}
              className="w-5 h-0.5 bg-white rounded"
              style={{ originY: "center" }}
              transition={{ duration: 0.2 }}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown (Framer Motion Drawer) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="w-full border-t border-white/10 shadow-2xl backdrop-blur-xl z-50 flex flex-col gap-2 overflow-hidden mt-3"
            style={{
              background: "rgba(9, 13, 22, 0.95)",
              borderBottom: "1px solid rgba(255, 255, 255, 0.12)",
              padding: "1.5rem 2rem",
            }}
          >
            {navItems.map((item) => {
              const active = isActive(item.paths);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 p-3 rounded-xl font-semibold text-[14px] text-decoration-none transition-all duration-200"
                  style={{
                    background: active ? "rgba(99, 102, 241, 0.1)" : "transparent",
                    border: active ? "1px solid rgba(99, 102, 241, 0.2)" : "1px solid transparent",
                    color: active ? "#ffffff" : "#cbd5e1",
                  }}
                >
                  <div style={{ color: active ? "#818cf8" : "#94a3b8" }}>
                    {item.icon}
                  </div>
                  <span>{item.label}</span>
                </Link>
              );
            })}

            <div className="h-px bg-white/10 my-2" />

            {/* Mobile More Apps Button */}
            <button
              onClick={() => {
                setIsOpen(false);
                openMoreApps();
              }}
              id="mobile-more-apps-btn"
              className="flex items-center gap-3 p-3 rounded-xl font-bold text-[14px] text-white transition-all cursor-pointer border-0"
              style={{
                background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(168, 85, 247, 0.2))",
                border: "1px solid rgba(99, 102, 241, 0.4)",
              }}
            >
              <div className="p-1.5 rounded-lg bg-indigo-500/30 text-indigo-300 flex items-center justify-center">
                <Apps sx={{ fontSize: 20 }} />
              </div>
              <span className="flex-1 text-left">More Applications</span>
              <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-500 text-white font-extrabold">
                {apps.length}
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default CustomNavbar;
